import React, { useState, useEffect } from "react";

import { useParams, useNavigate, useLocation } from "react-router-dom";
import PatientScriptInfo from "../../../global/view/patient-script-infoForm";
import { fetchProfileFamily, fetchProfileInfo, fetchScriptDetails } from "../../../../api/dispensary/script/processScriptApi.js";

import ReadOnlyInput from "../../../../components/ReadOnlyInput";
import Button from "../../../../components/Button";
import modalStyles from "../../../../css/components/overlaymodal-animations.module.css";
import styles from "../css/medical-aid-amend-modal.module.css";
import ReversalAcceptedModal from "./ReversalAcceptedModal";
import { getLatestClaim } from "../../../../api/medikredit/claimApi";
import { reverseClaim } from "../../../../api/medikredit/reversalApi";

/** dd/MM/yyyy, matching the DatePicker display format used elsewhere. */
function formatDateZA(value) {
  const d = value ? new Date(value) : new Date();
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

export default function MedicalAidAmmendFilter() {
  const { scriptNo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { profileNo, patientId } = location.state || {};

  const [showSelectColumn, setShowSelectColumn] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showAcceptedModal, setShowAcceptedModal] = useState(false);
  const [amendDisabled, setAmendDisabled] = useState(false);
  const [details, setDetails] = useState(null); // real script details from the DB
  const [detailsError, setDetailsError] = useState(null);
  const [latestClaim, setLatestClaim] = useState(null); // real MediKredit switch reference (auth number)
  const [reversing, setReversing] = useState(false);
  const [reversalError, setReversalError] = useState(null);
  const [reversalMessages, setReversalMessages] = useState([]); // MK RMRs on a rejected reversal
  const [reversalData, setReversalData] = useState(null); // real POST /api/medikredit/reversal response

  // The route param is the prescription id (same convention as fetchScriptDetails).
  const prescriptionId = scriptNo;

  // Load the real script (drugs, payment type, chronic/repeat flags, doc URL).
  useEffect(() => {
    let cancelled = false;
    if (!prescriptionId) return;
    (async () => {
      try {
        const data = await fetchScriptDetails(prescriptionId);
        if (!cancelled) setDetails(data || null);
      } catch (e) {
        if (!cancelled) setDetailsError("Script not found");
      }
    })();
    return () => { cancelled = true; };
  }, [prescriptionId]);

  // Real switch reference for the original claim (+ reversal-window info) if any.
  useEffect(() => {
    let cancelled = false;
    if (!prescriptionId) return;
    (async () => {
      try {
        const res = await getLatestClaim(prescriptionId);
        if (!cancelled && res?.found) setLatestClaim(res);
      } catch (_) {
        // no claim on file yet — leave blank, not a hard error
      }
    })();
    return () => { cancelled = true; };
  }, [prescriptionId]);

  const extraFields = [
    { label: "Script Number", value: scriptNo || "N/A", readOnly: true },
  ];

  // Determine if Amend/Rewrite should be disabled based on beneficiary vs main member logic
  useEffect(() => {
    const checkBeneficiaryEnabled = async () => {
      if (!profileNo || !patientId) return;
      try {
        const [info, family] = await Promise.all([
          fetchProfileInfo(profileNo),
          fetchProfileFamily(profileNo),
        ]);

        const mainIdStr = info?.mainPatient?.id != null
          ? info.mainPatient.id?.toString?.() ?? String(info.mainPatient.id)
          : null;

        const isBeneficiary = mainIdStr ? String(patientId) !== mainIdStr : false;
        const profileEnabled = info?.enabled ?? false;

        if (!profileEnabled) {
          // Disabled profile blocks both main member and beneficiaries
          setAmendDisabled(true);
          return;
        }

        if (isBeneficiary) {
          const target = (family || []).find((m) => String(m.id) === String(patientId));
          const enabledBen = target?.enabledbeneficiary ?? false;
          setAmendDisabled(!enabledBen);
        } else {
          // Main member is allowed when profile is enabled
          setAmendDisabled(false);
        }
      } catch (e) {
        console.error("Failed to load family for medical-aid-ammend-filter beneficiary check", e);
      }
    };

    checkBeneficiaryEnabled();
  }, [profileNo, patientId]);

  if (detailsError) {
    return <PatientScriptInfo title="Script not found" />;
  }

  const handleAmendScript = () => {
    if (!showSelectColumn) {
      setShowSelectColumn(true);
      return;
    }
    setShowModal(true); // Show modal when "Proceed" is clicked
  };

  const handleCloseModal = () => {
    setShowModal(false); // Close modal
    setReversalError(null);
    setReversalMessages([]);
  };

  // Reverse the original claim (tx_cd 10) — only a successful reversal opens the
  // Reversal Accepted modal; a rejection/failure surfaces an error instead.
  const handleConfirmAmend = async () => {
    setReversalError(null);
    setReversalMessages([]);
    setReversing(true);
    try {
      const res = await reverseClaim(prescriptionId);
      if (res?.success && res.reversed) {
        setReversalData(res);
        setShowModal(false);
        setShowAcceptedModal(true);
      } else {
        // MediKredit reached but rejected the reversal — show the reason text and
        // the actual RMR reject codes it returned (accreditation requires these).
        setReversalError(
          res?.resultText
            ? `MediKredit rejected the reversal (${res.resultText}).`
            : res?.message || "MediKredit rejected the reversal."
        );
        setReversalMessages(Array.isArray(res?.messages) ? res.messages : []);
      }
    } catch (e) {
      setReversalError(e?.response?.data?.message || e.message || "Reversal failed.");
      setReversalMessages(e?.response?.data?.messages || []);
    } finally {
      setReversing(false);
    }
  };

  // Pass the kept drugs (with their prescriptionDrugId/fromOriginal tags) and the
  // removed ones to the amend form via nav state — the form never reads selectedRows.
  const handleRewrite = async () => {
    setShowAcceptedModal(false);

    let scriptDetails = details;
    if (!scriptDetails) {
      try {
        scriptDetails = await fetchScriptDetails(prescriptionId);
      } catch (e) {
        console.error("Failed to load full script details for medical-aid amend", e);
        scriptDetails = null;
      }
    }

    const fullDrugs = Array.isArray(scriptDetails?.drugs) ? scriptDetails.drugs : [];
    const script = scriptDetails?.script || {};

    const selected = fullDrugs.filter((_, idx) => !!selectedRows[idx]);
    const removed = fullDrugs.filter((_, idx) => !selectedRows[idx]);

    const medsForAmend = selected.map((d, i) => {
      const codeStr = (d.icd10 || "").trim();
      const codes = codeStr ? codeStr.split(/\s*,\s*/) : [];
      return {
        id: i + 1,
        drugName: d.drugName || "",
        strength: d.strength || "",
        quantity: d.quantity || "1",
        packSize: d.packSize || "",
        icd10Options: codes,
        icd10: codes[0] || "",
        nappiCode: d.nappiCode || "",
        instructions: d.instructions || "",
        costPrice: d.costPrice || "",
        deliveryOption: "Collect",
        prescriptionDrugId: d.prescriptionDrugId || null,
        sku: d.sku || null,
        fromOriginal: true,
      };
    });

    const removedDrugsMeta = removed.map((d) => ({
      prescriptionDrugId: d.prescriptionDrugId || null,
      sku: d.sku || null,
      quantity: d.quantity || 0,
    }));

    navigate("/customer/dispensary/amend-script/new-script", {
      state: {
        scriptNo,
        selectedRows,
        amendFlow: true,
        profileNo: profileNo || scriptDetails?.patient?.profileNo || null,
        patientId: patientId || scriptDetails?.patient?.patientId || null,
        medications: medsForAmend,
        originalDrugs: fullDrugs,
        removedDrugs: removedDrugsMeta,
        initialScriptType: script.scriptType || "",
        initialIsChronic: !!script.isChronic,
        initialIsRepeat: (script.repeatCount ?? 0) > 0,
        initialRepeatCount: Number(script.repeatCount ?? 0) || 0,
        processcriptnumber: script.processscriptnumber || scriptNo,
        scriptDocUrl: script.docurl || null,
        // Keep it a medical-aid script on rewrite, else the form defaults to Cash
        // and the amend drops the MediKredit re-claim.
        paymentType: script.paymentType || "Medical Aid",
        medicalAidNumber: scriptDetails?.medicalAid?.number || "",
      },
    });
  };

  // NCND ("No Claim, No Dispense"): claim already reversed, nothing left to submit —
  // just return to the dispensary landing page.
  const handleNCND = () => {
    setShowAcceptedModal(false);
    navigate("/customer/dispensary");
  };

  const handleCheckboxChange = (drugIndex) => {
    setSelectedRows((prev) => ({
      ...prev,
      [drugIndex]: !prev[drugIndex],
    }));
  };

  const extraColumns = [
    { header: "Status", accessor: "status" },
    { header: "Reason", accessor: "reason" },
  ];

  return (
    <>
      <PatientScriptInfo
        hideFields={["patient", "medicalAid", "medicalAidNumber", ...(location.state?.hideFields || [])]}
        extraFields={extraFields}
        extraColumns={extraColumns}
        title="Medical Aid Amend Script Details"
        customButtons={[
          {
            label: showSelectColumn ? "Proceed" : "Amend Script",
            onClick: handleAmendScript,
            variant: "primary",
            disabled: amendDisabled,
          },
        ]}
        showSelectColumn={showSelectColumn}
        selectedRows={selectedRows}
        onCheckboxChange={handleCheckboxChange}
      />

      {showModal && (
        <div className={`${modalStyles.modalOverlay} ${showModal ? modalStyles.show : modalStyles.hide}`}>
          <div className={`${modalStyles.modalContent} ${styles.modalContent} ${showModal ? modalStyles.stepEnter : modalStyles.stepExit}`}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalHeading}>Claim Reversal</h2>
              <button className={modalStyles.closeButton} onClick={handleCloseModal}>
                &times;
              </button>
            </div>
            <div className={styles.content}>
              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Script Number</label>
                  <div className={styles.compactField}>
                    <ReadOnlyInput value={scriptNo || ""} />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Date</label>
                  <div className={styles.compactField}>
                    {/* Static — the date the original script/claim was submitted. */}
                    <ReadOnlyInput value={formatDateZA(latestClaim?.submittedAt || details?.script?.date)} />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.label}>Switch Reference</label>
                  <div className={styles.compactField}>
                    {/* Real MediKredit auth number from the original accepted claim. */}
                    <ReadOnlyInput
                      value={latestClaim?.authNumber || "No claim on file for this script"}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Date Submitted</label>
                  <div className={styles.compactField}>
                    {/* Static — today, the amendment is being submitted now. */}
                    <ReadOnlyInput value={formatDateZA(new Date())} />
                  </div>
                </div>
              </div>
              {/* Reversal window lapsed — block the amend upfront. */}
              {latestClaim && latestClaim.reversible === false && (
                <div className={styles.errorBanner}>
                  {latestClaim.reversalBlockedReason || "This claim is outside the scheme's reversal window and can no longer be reversed."}
                </div>
              )}
              {reversalError && (
                <div className={styles.errorBanner}>{reversalError}</div>
              )}
              {/* MediKredit RMRs returned on a rejected reversal. */}
              {reversalMessages.length > 0 && (
                <div className={styles.errorBanner}>
                  <strong>MediKredit RMRs</strong>
                  <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                    {reversalMessages.map((m, i) => (
                      <li key={i}>
                        {m.line ? `Line ${m.line}: ` : ""}
                        {m.desc} ({m.code})
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className={styles.buttonContainer}>
                <Button
                  variant="secondary"
                  className={styles.smallButton}
                  onClick={handleConfirmAmend}
                  disabled={reversing || !latestClaim?.authNumber || latestClaim?.reversible === false}
                >
                  {reversing && <span className={styles.spinner} />}
                  {reversing ? "Reversing..." : "Ammend Script"}
                </Button>
                <Button variant="primary" className={styles.smallButton} onClick={handleCloseModal} disabled={reversing}>
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAcceptedModal && (
        <ReversalAcceptedModal
          onClose={() => setShowAcceptedModal(false)}
          onRewrite={handleRewrite}
          onNCND={handleNCND}
          data={{
            // Reversal transaction status (res=A → Reversed) and per-line status.
            status: reversalData?.reversed
              ? `Reversed (${reversalData?.result || "A"})`
              : (reversalData?.resultText || "—"),
            itemStatus: Array.isArray(reversalData?.items) && reversalData.items.length
              ? reversalData.items.map((it) => `#${it.number}: ${it.status}`).join(", ")
              : "—",
            // Same transaction details as the reversed claim (MediKredit requirement).
            claimNumber: reversalData?.transactionNumber || latestClaim?.transactionNumber || "—",
            memberNumber: reversalData?.memberNumber || "—",
            // Financials of the claim being reversed.
            financials: reversalData?.financials || null,
            // MK RMRs returned on the reversal.
            messages: reversalData?.messages || [],
            // Fund authorization number (AUTHS/fund) of the claim that was just
            // reversed — only populated by MediKredit for switch-out transactions.
            authorizationNumber: reversalData?.reversedClaimFundAuth || "Not switched to scheme",
            processed: new Date().toISOString().slice(0, 19).replace('T', ' '),
            // This reversal transaction's own HealthNet switch reference (AUTHS/hnet).
            switchReference: reversalData?.authNumber || latestClaim?.authNumber || "",
          }}
        />
      )}
    </>
  );
}